import React, { useContext, useState, useEffect } from "react";
import { Modal, TextInput, Label, Select } from "flowbite-react";
import Swal from "sweetalert2";
import axios from "axios";
import DataTable from "react-data-table-component";
import engineGif from "../image/engine.gif";
import Button_submit from "../components/Button_submit";
import GlobalContext from "../connections/GlobalContext";
import Head_data from "../components/Head_data";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Logo from "../image/logo.jpg";

const Income = ({ theme }) => {
  const { globalValue } = useContext(GlobalContext);

  // Main states
  const [loading, setLoading] = useState(false);
  const [incomes, setIncomes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add");

  // Payment management states
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editPaymentModalOpen, setEditPaymentModalOpen] = useState(false);
  const [paymentDetailsModalOpen, setPaymentDetailsModalOpen] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    income_id: "",
    amount: "",
    remark: "",
    date: "",
    payment_type: "",
  });
  const [editPaymentData, setEditPaymentData] = useState({
    payment_id: "",
    amount: "",
    remark: "",
    date: "",
    payment_type: "",
  });
  const [paymentDetails, setPaymentDetails] = useState([]);

  // Form and UI states
  const [formData, setFormData] = useState({
    id: null,
    category_id: "",
    amount: "",
    date: "",
    income_name: "",
    payment_type: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredIncomes, setFilteredIncomes] = useState([]);
  const [pdfpopup, setPdfpopup] = useState(false);
  const [pdf_data, setPdfData] = useState(null);
  const [categoryIncomeNames, setCategoryIncomeNames] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Helper functions
  const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm = today.getMonth() + 1;
    let dd = today.getDate();
    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;
    return `${yyyy}-${mm}-${dd}`;
  };

  const convertToYYYYMMDD = (dateStr) => {
    if (!dateStr) return getTodayDate();
    try {
      if (dateStr.includes('/')) {
        const [dd, mm, yyyy] = dateStr.split('/');
        return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
      }
      return dateStr;
    } catch {
      return getTodayDate();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      if (dateStr.includes("-")) {
        const [yyyy, mm, dd] = dateStr.split("-");
        return `${dd}/${mm}/${yyyy}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  // Data fetching functions
  const fetchIncomes = () => {
    setLoading(true);
    const branch = sessionStorage.getItem("branch");

    axios
      .get(`${globalValue}/income_display`, { params: { branch } })
      .then((res) => {
        const processedIncomes = (Array.isArray(res.data) ? res.data : []).map(income => ({
          ...income,
          paid_amount: parseFloat(income.paid_amount || 0),
          pending_amount: parseFloat(income.pending_amount || 0),
          payment_entries: Array.isArray(income.payment_entries) ? income.payment_entries : []
        }));
        setIncomes(processedIncomes);
        setFilteredIncomes(processedIncomes);
      })
      .catch((err) => {
        console.error(err);
        setIncomes([]);
        setFilteredIncomes([]);
      })
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    setLoading(true);
    axios
      .get(`${globalValue}/category_income_display`)
      .then((res) => {
        setCategories(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error(err);
        setCategories([]);
      })
      .finally(() => setLoading(false));
  };

  const fetchPaymentDetails = async (incomeId) => {
    try {
      const income = incomes.find(income => income.id == incomeId);
      setPaymentDetails(Array.isArray(income?.payment_entries) ? income.payment_entries : []);
      setSelectedIncome(income || null);
    } catch (err) {
      console.error('Error fetching payment details:', err);
      setPaymentDetails([]);
      setSelectedIncome(null);
    }
    setPaymentDetailsModalOpen(true);
  };

  // Effects
  useEffect(() => {
    fetchIncomes();
    fetchCategories();
    setFormData(prev => ({ ...prev, date: getTodayDate() }));
  }, []);

  useEffect(() => {
    let filtered = (Array.isArray(incomes) ? incomes : []).filter(
      (row) =>
        (row.category_name && row.category_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.income_name && row.income_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    // Apply date range filter
    const convertToYYYYMMDD = (dateStr) => {
      if (!dateStr) return "";
      if (dateStr.includes("/")) {
        const [dd, mm, yyyy] = dateStr.split("/");
        return `${yyyy}-${mm}-${dd}`;
      }
      return dateStr;
    };
    
    if (startDate) {
      filtered = filtered.filter(row => {
        const rowDate = convertToYYYYMMDD(row.date);
        return rowDate && rowDate >= startDate;
      });
    }
    
    if (endDate) {
      filtered = filtered.filter(row => {
        const rowDate = convertToYYYYMMDD(row.date);
        return rowDate && rowDate <= endDate;
      });
    }
    
    setFilteredIncomes(filtered);
  }, [searchTerm, incomes, startDate, endDate]);

  useEffect(() => {
    if (formData.category_id) {
      const uniqueNames = [
        ...new Set(
          (Array.isArray(filteredIncomes) ? filteredIncomes : [])
            .filter(item => item.category_id === Number(formData.category_id))
            .map(item => item.income_name)
            .filter(Boolean)
        ),
      ];
      setCategoryIncomeNames(uniqueNames);
    }
  }, [formData.category_id, incomes, categories, filteredIncomes]);

  // CRUD Operations
  const handleSubmit = async () => {
    if (!formData.category_id || !formData.amount || !formData.date || !formData.income_name) {
      Swal.fire("Error", "All fields are required", "error");
      return;
    }

    const url = formData.id ? `${globalValue}/income_edit` : `${globalValue}/income_add`;
    setLoading(true);

    try {
      const payload = { ...formData, branch: sessionStorage.getItem("branch") };
      const res = await axios.post(url, payload);

      if (res.data.success === 1) {
        Swal.fire("Success", res.data.message, "success");
        fetchIncomes();
        setModalOpen(false);
        setFormData({
          id: null,
          category_id: "",
          amount: "",
          date: getTodayDate(),
          income_name: "",
          payment_type: "",
        });
      } else {
        Swal.fire("Error", res.data.message, "error");
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      id: row.id,
      category_id: row.category_id,
      amount: row.amount,
      date: row.date?.includes("/") ? row.date.split("/").reverse().join("-") : row.date,
      income_name: row.income_name,
      payment_type: row.payment_type,
    });
    setMode("edit");
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios.post(`${globalValue}/income_delete`, { id })
          .then((res) => {
            if (res.data.success === 1) {
              Swal.fire("Deleted!", res.data.message, "success");
              fetchIncomes();
            } else {
              Swal.fire("Error", res.data.message, "error");
            }
          })
          .catch((err) => Swal.fire("Error", err.message, "error"))
          .finally(() => setLoading(false));
      }
    });
  };

  // Payment handlers
  const handlePaymentSubmit = async () => {
    if (!paymentFormData.amount || parseFloat(paymentFormData.amount) <= 0) {
      Swal.fire("Error", "Amount must be greater than 0", "error");
      return;
    }
    if (!paymentFormData.date || !paymentFormData.payment_type) {
      Swal.fire("Error", "Date  or payment type are required", "error");
      return;
    }

    const pendingAmount = parseFloat(selectedIncome?.pending_amount || 0);
    const paymentAmount = parseFloat(paymentFormData.amount);

    if (paymentAmount > pendingAmount) {
      Swal.fire("Error", `Payment cannot exceed pending amount: Rs. ${pendingAmount.toLocaleString()}`, "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        income_id: parseInt(selectedIncome.id),
        amount: paymentAmount,
        remark: paymentFormData.remark || "",
        date: paymentFormData.date,
        payment_type: paymentFormData.payment_type || "",
      };

      const res = await axios.post(`${globalValue}/receiveIncomePayment`, payload);
      if (res.data.success === 1) {
        Swal.fire("Success", "Payment received successfully", "success");
        setPaymentModalOpen(false);
        setPaymentDetailsModalOpen(false);
        setPaymentFormData({ income_id: "", amount: "", remark: "", date: getTodayDate(), payment_type: "" });
        fetchIncomes();
      } else {
        Swal.fire("Error", res.data.message || "Failed to record payment", "error");
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Failed to record payment", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = (income) => {
    setSelectedIncome(income);
    setPaymentFormData({
      income_id: income.id,
      amount: "",
      remark: "",
      date: getTodayDate(),
      payment_type: "",
    });
    setPaymentModalOpen(true);
  };

  const handleEditPayment = (payment) => {
    setEditPaymentData({
      payment_id: payment.payment_id || payment.id,
      amount: payment.amount,
      remark: payment.remark || "",
      date: convertToYYYYMMDD(payment.date),
      payment_type: payment.payment_type || "",
    });
    setEditPaymentModalOpen(true);
  };

  const handleUpdatePayment = async () => {
    if (!editPaymentData.amount || parseFloat(editPaymentData.amount) <= 0) {
      Swal.fire("Error", "Amount must be greater than 0", "error");
      return;
    }
    if (!editPaymentData.date || !editPaymentData.payment_type) {
      Swal.fire("Error", "Date  or payment type are required", "error");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        payment_id: parseInt(editPaymentData.payment_id),
        amount: parseFloat(editPaymentData.amount),
        remark: editPaymentData.remark || "",
        date: editPaymentData.date,
        payment_type: editPaymentData.payment_type || "",
      };
      const res = await axios.put(`${globalValue}/editIncomePayment`, payload);
      if (res.data.success === 1) {
        Swal.fire("Success", "Payment updated successfully", "success");
        setEditPaymentModalOpen(false);
        setPaymentModalOpen(false);
        setPaymentDetailsModalOpen(false);
        setEditPaymentData({ payment_id: "", amount: "", remark: "", date: "" });
        fetchIncomes();
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Failed to update payment", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = (paymentId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios.delete(`${globalValue}/deleteIncomePayment/${paymentId}`)
          .then((res) => {
            if (res.data.success === 1) {
              Swal.fire("Deleted!", "Payment deleted successfully", "success");
              setPaymentDetailsModalOpen(false);
              setEditPaymentModalOpen(false);
              setPaymentModalOpen(false);
              fetchIncomes();
            } else {
              Swal.fire("Error", res.data.message || "Failed to delete payment", "error");
            }
          })
          .catch((err) => Swal.fire("Error", err.message, "error"))
          .finally(() => setLoading(false));
      }
    });
  };

  // PDF functions
  const openPdfModal = (row) => {
    setPdfData(row);
    setPdfpopup(true);
  };

  const handlePrint = () => {
    const content = document.getElementById("pdf-content");
    if (!content) return;

    html2canvas(content, { scale: 2, useCORS: true }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
      let remainingHeight = pdfHeight - pdf.internal.pageSize.getHeight();
      while (remainingHeight > 0) {
        position -= pdf.internal.pageSize.getHeight();
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        remainingHeight -= pdf.internal.pageSize.getHeight();
      }

      pdf.save(`Income_${pdf_data?.id || "receipt"}.pdf`);
      setPdfpopup(false);
    });
  };

  // Enhanced columns with status badges
  const columns = [
    { name: "S.No", selector: (row) => row.sno, sortable: false, width: "80px" },
    { name: "Category", selector: (row) => row.category_name || "—", sortable: true },
    {
      name: "Income Name",
      selector: (row) => row.income_name || "—",
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span>{row.income_name || "—"}</span>
        </div>
      )
    },
    {
      name: "Total Amount",
      selector: (row) => `Rs. ${parseFloat(row.amount || 0).toLocaleString()}`,
      sortable: true,
      width: "140px"
    },
    {
      name: "Paid",
      selector: (row) => `Rs. ${parseFloat(row.paid_amount || 0).toLocaleString()}`,
      sortable: true,
      width: "120px"
    },
    {
      name: "Pending",
      selector: (row) => `Rs. ${parseFloat(row.pending_amount || 0).toLocaleString()}`,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span className="font-bold text-orange-600">
          Rs. {parseFloat(row.pending_amount || 0).toLocaleString()}
        </span>
      )
    },
    {
      name: "Status",
      width: "120px",
      cell: (row) => {
        const pending = parseFloat(row.pending_amount || 0);
        const paid = parseFloat(row.paid_amount || 0);

        if (pending === 0 && paid > 0) {
          return (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
              ✓ Collected
            </span>
          );
        } else if (paid > 0 && pending > 0) {
          return (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
              ⏳ Partial
            </span>
          );
        } else {
          return (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
              📋 Pending
            </span>
          );
        }
      }
    },
    {
      name: "Payment Type",
      selector: (row) => row.payment_type || "—",
      sortable: true,
      width: "110px"
    },
    { name: "Date", selector: (row) => row.date || "—", sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-white bg-blue-700 hover:bg-blue-800 rounded-md shadow-xl text-xs transition"
            title="Edit"
          >
            <i className="fa fa-edit"></i>
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-2 text-white bg-yellow-500 hover:bg-yellow-800 rounded-md shadow-xl text-xs transition"
            title="Delete"
          >
            <i className="fa fa-trash"></i>
          </button>
          <button
            onClick={() => openPdfModal(row)}
            className="p-2 text-white bg-green-600 hover:bg-green-800 rounded-md shadow-xl text-xs transition"
            title="Print Receipt"
          >
            <i className="fa fa-print"></i>
          </button>
          {parseFloat(row.pending_amount || 0) > 0 && (
            <button
              onClick={() => handlePayNow(row)}
              className="p-2 text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-md shadow-xl text-xs font-semibold transition"
              title={`Collect Rs. ${parseFloat(row.pending_amount || 0).toLocaleString()}`}
            >
              <i className="fa fa-money-bill-wave"></i>
            </button>
          )}
          <button
            onClick={() => fetchPaymentDetails(row.id)}
            className="p-2 text-white bg-purple-600 hover:bg-purple-700 rounded-md shadow-xl text-xs transition"
            title="Payment History"
          >
            <i className="fa fa-history"></i>
          </button>
        </div>
      ),
      width: "300px"
    },
  ];

  // Enhanced payment columns
  const paymentColumns = [
    {
      name: "S.No",
      selector: (row, idx) => idx + 1,
      sortable: false,
      width: "70px",
      center: true
    },
    {
      name: "Amount",
      selector: (row) => `₹${parseFloat(row.amount || 0).toLocaleString()}`,
      sortable: true,
      cell: (row) => (
        <span className="font-semibold text-green-600">
          ₹{parseFloat(row.amount || 0).toLocaleString()}
        </span>
      )
    },
    {
      name: "Remark",
      selector: (row) => row.remark || "—",
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-sm">{row.remark || "No remark"}</span>
        </div>
      )
    },
    {
      name: "Payment Type",
      selector: (row) => row.payment_type || "—",
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-sm">{row.payment_type || "—"}</span>
        </div>
      )
    },
    {
      name: "Date",
      selector: (row) => row.date || "—",
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span>{formatDate(row.date)}</span>
        </div>
      )
    },
    {
      name: "Actions",
      cell: (rowData) => (
        <div className="flex gap-1">
          <button
            onClick={() => handleEditPayment(rowData)}
            className="p-1.5 text-white bg-blue-600 hover:bg-blue-700 rounded text-xs shadow-sm transition"
            title="Edit Payment"
          >
            <i className="fa fa-edit"></i>
          </button>
          <button
            onClick={() => handleDeletePayment(rowData.payment_id || rowData.id)}
            className="p-1.5 text-white bg-red-600 hover:bg-red-700 rounded text-xs shadow-sm transition"
            title="Delete Payment"
          >
            <i className="fa fa-trash"></i>
          </button>
        </div>
      ),
      width: "120px",
      center: true
    },
  ];

  // Theme-aware table styles
  const customStyles = {
    table: {
      style: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        width: "100%",
        border: theme === "dark" ? "solid 1px rgb(63, 62, 62)" : "solid 1px rgb(226, 232, 240)",
      },
    },
    headCells: {
      style: {
        backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
        color: theme === "dark" ? "white" : "black",
        fontWeight: "bold",
      },
    },
    cells: {
      style: {
        color: theme === "dark" ? "white" : "black",
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        border: theme === "dark" ? "solid 1px rgb(63, 62, 62)" : "solid 1px rgb(255, 255, 255)",
      },
    },
    pagination: {
      style: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        color: theme === "dark" ? "white" : "black",
        borderTop: theme === "dark" ? "solid 1px white" : "solid 1px rgb(226, 232, 240)",
      },
      pageButtonsStyle: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        color: theme === "dark" ? "white" : "black",
        fill: theme === "dark" ? "white" : "black",
        "&:hover": {
          backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
        },
      },
    },
  };

  return (
    <div>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
          <img src={engineGif} alt="Loading..." className="w-24 h-24" />
        </div>
      )}

      <div className={`md:ml-[250px] w-full md:w-[calc(100%-250px)] mt-16 h-[92vh] flex justify-center items-center box-border ${theme === "dark" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"}`}>
        <div className={`w-[95%] md:w-[98%] h-[95%] px-4 py-2 rounded-xl shadow-xl overflow-auto ${theme === "dark" ? "bg-gray-800 text-gray-200" : "bg-white text-gray-800"}`}>

          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
            <Head_data
              data="Manage Income"
              font=""
              size="3xl"
              color={theme === "dark" ? "text-gray-200" : "text-gray-800"}
            />
            <div className="flex w-full md:w-auto gap-2">
              <button
                className="px-6 py-2 text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg hover:from-emerald-700 hover:to-teal-700 shadow-lg transition font-medium"
                onClick={() => {
                  setFormData({
                    id: null,
                    category_id: "",
                    amount: "",
                    date: getTodayDate(),
                    income_name: "",
                    payment_type: "",
                  });
                  setMode("add");
                  setModalOpen(true);
                }}
              >
                <i className="fa fa-plus mr-2"></i>Add Income
              </button>
            </div>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search by category or income name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-96 px-4 my-2 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition duration-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />

          {/* Date Filters */}
          <div className="flex flex-wrap gap-4 my-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
                className="px-4 py-2 text-white bg-gray-500 rounded-lg hover:bg-gray-600 transition"
              >
                Clear Dates
              </button>
            </div>
          </div>

          {/* Main DataTable */}
          <DataTable
            columns={columns}
            data={Array.isArray(filteredIncomes) ? filteredIncomes.map((row, idx) => ({ ...row, sno: idx + 1 })) : []}
            pagination
            paginationPerPage={10}
            striped
            highlightOnHover
            customStyles={customStyles}
            noDataComponent={
              <div className="p-12 text-center">
                <i className="fa fa-money-bill-wave text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-500 mb-2">No income records found</h3>
                <p className="text-gray-400">Add your first income entry to get started</p>
              </div>
            }
          />

          {/* Pending Total */}
          <div className="mt-4 flex justify-end">
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/20 p-4 rounded-xl border border-orange-200 dark:border-orange-800">
              <span className="font-semibold mr-2">Total Pending Amount:</span>
              <span className="font-bold text-xl text-orange-700 dark:text-orange-400">
                ₹ {filteredIncomes.reduce((sum, inc) => sum + parseFloat(inc.pending_amount || 0), 0).toLocaleString()}
              </span>
            </div>
          </div>

          {/* MAIN INCOME MODAL */}
          <Modal show={modalOpen} onClose={() => setModalOpen(false)} size="md">
            <Modal.Header>{mode === "add" ? "Add Income" : "Edit Income"}</Modal.Header>
            <Modal.Body>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    id="category"
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="income-name">Income Name *</Label>
                  <div>
                    <input
                      list="incomeSuggestions"
                      type="text"
                      id="income-name"
                      value={formData.income_name}
                      onChange={(e) => setFormData({ ...formData, income_name: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                      placeholder="Enter income name or select from suggestions"
                    />
                    <datalist id="incomeSuggestions">
                      {categoryIncomeNames.map((name, idx) => <option key={idx} value={name} />)}
                    </datalist>
                  </div>
                </div>

                <div>
                  <Label htmlFor="amount">Amount *</Label>
                  <TextInput
                    id="amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                    disabled={sessionStorage.getItem("position") !== "superadmin"}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button_submit
                onClick={handleSubmit}
                data={`${mode === "add" ? "Submit" : "Update"}`}
                type="submit"
                className="ms-auto px-6"
              >
                {mode === "add" ? "Submit" : "Update"}
              </Button_submit>
            </Modal.Footer>
          </Modal>

          {/* ENHANCED PAYMENT MODAL - INCOME SPECIFIC */}
          <Modal show={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} size="lg">
            <Modal.Header>
              <div className="flex items-center gap-3">
                <i className="fa fa-money-bill-wave text-2xl text-emerald-600"></i>
                <div>
                  <h3 className="text-xl font-bold">Record Income Payment</h3>
                  <p className="text-sm text-gray-500">{selectedIncome?.income_name} - {selectedIncome?.category_name}</p>
                </div>
              </div>
            </Modal.Header>
            <Modal.Body>
              <div className="space-y-6">
                {/* Income Summary */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-6 rounded-xl border">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Income ID</p>
                      <p className="font-bold text-lg">{selectedIncome?.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Total Amount</p>
                      <p className="font-bold text-xl text-emerald-600">
                        ₹{parseFloat(selectedIncome?.amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Collected</p>
                      <p className="font-bold text-xl text-green-600">
                        ₹{parseFloat(selectedIncome?.paid_amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Pending</p>
                      <p className="font-bold text-2xl text-orange-600 bg-orange-100 px-3 py-1 rounded-full inline-block">
                        ₹{parseFloat(selectedIncome?.pending_amount || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Form */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="payment-amount" className="text-lg font-semibold">Payment Amount *</Label>
                    <TextInput
                      id="payment-amount"
                      type="number"
                      value={paymentFormData.amount}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                      placeholder="Enter payment amount"
                      className="text-2xl py-4"
                      helperText={
                        paymentFormData.amount && parseFloat(paymentFormData.amount) > parseFloat(selectedIncome?.pending_amount || 0)
                          ? "⚠️ Amount exceeds pending balance"
                          : `Maximum allowed: ₹${parseFloat(selectedIncome?.pending_amount || 0).toLocaleString()}`
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="payment-date">Payment Date</Label>
                    <input
                      id="payment-date"
                      type="date"
                      value={paymentFormData.date}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, date: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                      max={getTodayDate()}
                      disabled={sessionStorage.getItem("position") !== "superadmin"}

                    />
                  </div>

                  <div>
                    <Label htmlFor="payment-type">Payment Type</Label>
                    <select
                      id="payment-type"
                      value={paymentFormData.payment_type}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_type: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="">Select Payment Type</option>
                      <option value="Cash">💵 Cash</option>
                      <option value="UPI">📱 UPI</option>
                      <option value="Bank">🏦 Bank Transfer</option>
                      <option value="Credit">💳 Credit Card</option>
                      <option value="Cheque">📄 Cheque</option>
                      <option value="Other">📋 Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="payment-remark">Remark (Optional)</Label>
                    <textarea
                      id="payment-remark"
                      value={paymentFormData.remark}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, remark: e.target.value })}
                      placeholder="Payment details (Cash received, UPI reference, etc.)"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 resize-vertical"
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="flex justify-between w-full">
                <button
                  onClick={() => setPaymentModalOpen(false)}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePaymentSubmit}
                  disabled={!paymentFormData.amount || parseFloat(paymentFormData.amount) <= 0}
                  className="px-8 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center gap-2 shadow-lg font-semibold"
                >
                  <i className="fa fa-check-circle"></i>
                  Record Payment
                </button>
              </div>
            </Modal.Footer>
          </Modal>

          {/* EDIT PAYMENT MODAL */}
          <Modal show={editPaymentModalOpen} onClose={() => setEditPaymentModalOpen(false)}>
            <Modal.Header>Edit Payment</Modal.Header>
            <Modal.Body>
              <div className="space-y-4">
                <div>
                  <Label>Payment ID</Label>
                  <TextInput value={editPaymentData.payment_id} disabled />
                </div>
                <div>
                  <Label>Amount</Label>
                  <TextInput
                    type="number"
                    value={editPaymentData.amount}
                    disabled
                    onChange={(e) => setEditPaymentData({ ...editPaymentData, amount: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Remark</Label>
                  <TextInput
                    type="text"
                    value={editPaymentData.remark}
                    onChange={(e) => setEditPaymentData({ ...editPaymentData, remark: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Date</Label>
                  <input
                    type="date"
                    value={editPaymentData.date}
                    onChange={(e) => setEditPaymentData({ ...editPaymentData, date: e.target.value })}
                    className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <Label>Payment Type</Label>
                  <select
                    value={editPaymentData.payment_type}
                    onChange={(e) => setEditPaymentData({ ...editPaymentData, payment_type: e.target.value })}
                    className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value="">Select Payment Type</option>
                    <option value="Cash">💵 Cash</option>
                    <option value="UPI">📱 UPI</option>
                    <option value="Bank">🏦 Bank Transfer</option>
                    <option value="Credit">💳 Credit Card</option>
                    <option value="Cheque">📄 Cheque</option>
                    <option value="Other">📋 Other</option>
                  </select>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button_submit
                onClick={handleUpdatePayment}
                data="Update Payment"
                className="ms-auto"
              >
                Update Payment
              </Button_submit>
            </Modal.Footer>
          </Modal>

          {/* ENHANCED PAYMENT HISTORY MODAL */}
          <Modal show={paymentDetailsModalOpen} onClose={() => setPaymentDetailsModalOpen(false)} size="4xl">
            <Modal.Header>
              <div className="flex flex-col">
                <div className="flex items-center gap-3">
                  <i className="fa fa-history text-2xl text-emerald-600"></i>
                  <span className="text-2xl font-bold">Payment History</span>
                </div>
                <p className="text-sm text-gray-500 ml-9">{selectedIncome?.income_name} - {selectedIncome?.category_name}</p>
              </div>
            </Modal.Header>
            <Modal.Body>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl">
                <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border">
                  <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total Amount</p>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    ₹{parseFloat(selectedIncome?.amount || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border">
                  <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">Collected Amount</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    ₹{parseFloat(selectedIncome?.paid_amount || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border">
                  <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">Pending Amount</p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    ₹{parseFloat(selectedIncome?.pending_amount || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border">
                  <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total Payments</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {paymentDetails.length}
                  </p>
                </div>
              </div>

              {/* Payment Table */}
              <div className="border rounded-xl overflow-hidden shadow-sm">
                <DataTable
                  columns={paymentColumns}
                  data={Array.isArray(paymentDetails) ? paymentDetails : []}
                  pagination
                  paginationPerPage={5}
                  customStyles={customStyles}
                  noDataComponent={
                    <div className="p-12 text-center">
                      <i className="fa fa-receipt text-6xl text-gray-300 mb-4"></i>
                      <h3 className="text-xl font-semibold text-gray-500 mb-2">No payments recorded</h3>
                      <p className="text-gray-400">
                        {parseFloat(selectedIncome?.pending_amount || 0) > 0
                          ? "Record the first payment to track collection history"
                          : "All payments collected successfully!"
                        }
                      </p>
                    </div>
                  }
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                onClick={() => setPaymentDetailsModalOpen(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium"
              >
                Close
              </button>
              {parseFloat(selectedIncome?.pending_amount || 0) > 0 && (
                <button
                  onClick={() => {
                    setPaymentDetailsModalOpen(false);
                    handlePayNow(selectedIncome);
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 transition font-medium shadow-lg flex items-center gap-2"
                >
                  <i className="fa fa-money-bill-wave"></i>
                  Collect Payment
                </button>
              )}
            </Modal.Footer>
          </Modal>

          {/* ENHANCED PDF MODAL */}
          <Modal show={pdfpopup} size="6xl" popup={true} onClose={() => setPdfpopup(false)}>
            <Modal.Header className="bg-white text-gray-900 border-b border-gray-200"></Modal.Header>
            <Modal.Body className="flowbite-modal-b">
              <div id="pdf-content" className="border border-gray-500 mx-2 bg-white p-6 text-gray-800" style={{ minWidth: "700px" }}>
                <div className="flex items-center mb-6 pb-6 border-b-2 border-gray-300">
                  <div>
                    <img src={Logo} alt="JALS Automobiles Logo" className="h-20 w-auto" />
                  </div>
                  <div className="flex-1 text-center ml-8">
                    <h2 className="text-3xl font-bold uppercase tracking-wide mb-2">JALS AUTOMOBILES</h2>
                    {sessionStorage.getItem("branch") === "JBRANCH2025030" ? (
                      <>
                        <p className="text-lg mb-1">Door No: 6/63, Jals Road, Kuppottu Vilai,</p>
                        <p className="text-lg mb-1">Kadamacode, Melpuram, Pacode Post - 629168</p>
                        <p className="text-lg mb-2">Ph: 04651-264007 | Mob: 8300451644</p>
                        <p className="font-semibold text-lg">GSTIN: 33AMEPL9265E1Z1</p>
                      </>
                    ) : (
                      <>
                        <p className="text-lg mb-1">6/31 F, Karavilagam Road, Chennithottam Junction,</p>
                        <p className="text-lg mb-1">Vettuvanni, Marthandam - 629162</p>
                        <p className="text-lg mb-2">Ph: 04651-271220 | Mob: 9488495820</p>
                        <p className="font-semibold text-lg">GSTIN: 33AMEPL9265E1Z1</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center mb-8 pb-4 border-b-2 border-gray-300">
                  <h3 className="text-2xl font-bold tracking-wide">INCOME RECEIPT</h3>
                  <div className="text-right">
                    <p className="text-lg">
                      <span className="font-bold">Date: </span>
                      <span className="font-semibold">{formatDate(pdf_data?.date) || ""}</span>
                    </p>

                  </div>
                </div>

                <div className="mb-8 p-6 bg-gray-100 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Income Details:</h4>
                  <div className="grid grid-cols-2 gap-6 text-lg">
                    <div>
                      <p><strong>Category:</strong> {pdf_data?.category_name || "—"}</p>
                      <p><strong>Income:</strong> {pdf_data?.income_name || "—"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-emerald-600">
                        ₹{parseFloat(pdf_data?.amount || 0).toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {parseFloat(pdf_data?.pending_amount || 0) > 0
                          ? `Pending: ₹${parseFloat(pdf_data?.pending_amount || 0).toLocaleString()}`
                          : "Fully Collected"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-end mt-12 pt-8 border-t-2 border-gray-300">
                  <div>
                    <p className="font-bold text-lg mb-4">Prepared By</p>
                    <p className="text-gray-500">JALS Automobiles</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg mb-8">Authorized Signatory</p>
                    <p className="text-gray-500 text-sm">Accounts Department</p>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="flowbite-modal-b">
              <button
                onClick={handlePrint}
                className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition shadow-xl font-semibold text-lg"
              >
                <i className="fa fa-download mr-2"></i>Download PDF
              </button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default Income;
